import type { HTMLElement as ParsedElement, Node as ParsedNode, TextNode } from 'node-html-parser';
import { NodeType, parse } from 'node-html-parser';

/** One character per mention, so offsets agree whether the mention shows its placeholder or its value. */
export const MENTION_SENTINEL = '￼';

export const BLOCK_LEVEL_TAGS: ReadonlySet<string> = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
  'ul',
  'ol',
  'table',
  'tr',
  'td',
  'th',
  'blockquote',
  'pre',
  'section',
]);

export const SKIPPED_TAGS: ReadonlySet<string> = new Set(['script', 'style']);

export const isMentionElement = (dataType: string | null | undefined): boolean => dataType === 'mention';

/**
 * The block's "anchor text": text nodes verbatim, one sentinel per mention, a newline after each
 * block-level element and for <br>. Comment anchors are offsets into this string (spec §5.2).
 */
export const anchorTextOf = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  // `pre` must be parsed like the DOM parses it, not kept as raw text, to stay in step with the browser walker.
  const root = parse(html, { blockTextElements: { script: true, noscript: true, style: true } });
  let text = '';

  const walk = (node: ParsedNode): void => {
    if (node.nodeType === NodeType.TEXT_NODE) {
      text += (node as TextNode).text;
      return;
    }
    if (node.nodeType !== NodeType.ELEMENT_NODE) {
      return;
    }
    const element = node as ParsedElement;
    const tag = element.rawTagName?.toLowerCase() ?? '';
    if (SKIPPED_TAGS.has(tag)) {
      return;
    }
    if (isMentionElement(element.getAttribute('data-type'))) {
      text += MENTION_SENTINEL;
      return;
    }
    if (tag === 'br') {
      text += '\n';
      return;
    }
    element.childNodes.forEach(walk);
    if (BLOCK_LEVEL_TAGS.has(tag)) {
      text += '\n';
    }
  };

  root.childNodes.forEach(walk);
  return text;
};
